import { inject, Injector, runInInjectionContext } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { map, switchMap, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { UserService } from '../services/user.service';
import { Role } from '../models/user.model';

/**
 * Admin Guard
 * Protects routes that require ADMIN role
 * Checks both authentication and admin role in Firestore
 * Redirects to /auth/login if not authenticated
 * Redirects to /unauthorized if authenticated but not admin
 *
 * Usage in routes:
 * { path: 'admin', component: AdminComponent, canActivate: [adminGuard] }
 */
export const adminGuard: CanActivateFn = (route, state) => {
  const auth = inject(Auth);
  const router = inject(Router);
  const userService = inject(UserService);
  const injector = inject(Injector);

  return authState(auth).pipe(
    take(1),
    switchMap((user) => {
      if (!user || user.isAnonymous) {
        // Not authenticated (anonymous Merchant sessions do not count), redirect to login
        router.navigate(['/auth/login'], {
          queryParams: { returnUrl: state.url },
        });
        return of(false);
      }

      // Check if user has admin role
      return runInInjectionContext(injector, () =>
        userService.getUserById(user.uid).pipe(
          map((userData) => {
            if (userData && userData.role === Role.ADMIN) {
              return true;
            } else {
              // Authenticated but not admin
              router.navigate(['/unauthorized']);
              return false;
            }
          })
        )
      );
    })
  );
};
